import { useState, useEffect } from 'react';
import { Mail, Send, Users, Filter, Clock, CheckCircle, AlertCircle, Loader2, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MasterEmailCenter() {
  const [templates, setTemplates] = useState([]);
  const [history, setHistory] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [tab, setTab] = useState('compose');

  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [audience, setAudience] = useState('all');
  // Iter 175f — single-recipient send. When `audience === 'single'` the
  // UI shows a one-off email input that's sent to the backend in the
  // `single_email` body field.
  const [singleEmail, setSingleEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');

  const token = localStorage.getItem('masterToken') || localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [tplRes, histRes, statsRes] = await Promise.all([
        fetch(`${API}/master/emails/templates`, { headers }),
        fetch(`${API}/master/emails/history`, { headers }),
        fetch(`${API}/master/emails/stats`, { headers }),
      ]);
      if (tplRes.ok) setTemplates((await tplRes.json()).templates || []);
      if (histRes.ok) setHistory((await histRes.json()).campaigns || []);
      if (statsRes.ok) setStats(await statsRes.json());
    } catch (e) { console.error('Failed to load email data:', e); }
    finally { setLoading(false); }
  };

  const handleSend = async (testOnly = false) => {
    if (!subject && !selectedTemplate) { toast.error('Enter a subject or select a template'); return; }
    if (!testOnly && !content && !selectedTemplate) { toast.error('Enter email content'); return; }
    if (!testOnly && audience === 'single' && (!singleEmail.trim() || !singleEmail.includes('@'))) {
      toast.error('Enter a valid recipient email');
      return;
    }

    setSending(true);
    try {
      const res = await fetch(`${API}/master/emails/send`, {
        method: 'POST', headers,
        body: JSON.stringify({
          template_id: selectedTemplate || null,
          subject, title, content, audience,
          single_email: audience === 'single' ? singleEmail.trim() : undefined,
          test_only: testOnly,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message);
        loadData();
        if (!testOnly) { setSubject(''); setTitle(''); setContent(''); setSelectedTemplate(''); }
      } else {
        toast.error(data.detail || 'Failed to send');
      }
    } catch { toast.error('Failed to send emails'); }
    finally { setSending(false); }
  };

  const audiences = [
    { value: 'all', label: 'All Users', color: '#A78BFA' },
    { value: 'vendors', label: 'Vendors Only', color: '#FCD34D' },
    { value: 'consumers', label: 'Consumers Only', color: '#86EFAC' },
    { value: 'supporters', label: 'Supporters', color: '#F472B6' },
    { value: 'builders', label: 'Website Builders', color: '#3B82F6' },
    // Iter 175f — single-recipient send for support / one-off outreach.
    { value: 'single', label: 'Single Person', color: '#F97316' },
  ];

  if (loading) return <div className="flex justify-center py-20"><Loader2 size={32} className="animate-spin text-[#8B5CF6]" /></div>;

  return (
    <div className="space-y-6" data-testid="master-email-center">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white font-syne flex items-center gap-3" data-testid="email-center-title">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#8B5CF6] to-[#3B82F6] flex items-center justify-center"><Mail size={20} /></div>
            Email Center
          </h1>
          <p className="text-[#9CA3AF] text-sm mt-1">Send emails, promos, and announcements to your users</p>
        </div>
        {stats && (
          <div className="hidden md:flex items-center gap-4 text-sm">
            <div className="text-center"><p className="text-white font-bold">{stats.total_campaigns}</p><p className="text-[#64748B] text-[10px]">Campaigns</p></div>
            <div className="text-center"><p className="text-white font-bold">{stats.total_emails_sent}</p><p className="text-[#64748B] text-[10px]">Emails Sent</p></div>
            <div className="text-center"><p className="text-white font-bold">{stats.user_counts?.all || 0}</p><p className="text-[#64748B] text-[10px]">Total Users</p></div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-[#0E0E23]/60 rounded-xl p-1">
        {['compose', 'history'].map(t => (
          <button key={t} onClick={() => setTab(t)} className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all ${tab === t ? 'bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white shadow-lg' : 'text-[#9CA3AF]'}`} data-testid={`tab-${t}`}>
            {t === 'compose' ? 'Compose Email' : 'Send History'}
          </button>
        ))}
      </div>

      {tab === 'compose' && (
        <div className="space-y-5">
          {/* Template Selection */}
          <div className="bg-[#1E1E3A]/80 backdrop-blur-xl border border-white/5 rounded-2xl p-6">
            <Label className="text-white font-semibold mb-3 block">Email Template (optional)</Label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              <button onClick={() => setSelectedTemplate('')} className={`p-3 rounded-xl border text-left text-sm transition-all ${!selectedTemplate ? 'border-[#8B5CF6]/40 bg-[#8B5CF6]/10 text-white' : 'border-white/10 text-[#9CA3AF] hover:border-white/20'}`} data-testid="template-custom">
                <p className="font-medium">Custom Email</p>
                <p className="text-[10px] text-[#64748B]">Write your own</p>
              </button>
              {templates.map(t => (
                <button key={t.id} onClick={() => setSelectedTemplate(t.id)} className={`p-3 rounded-xl border text-left text-sm transition-all ${selectedTemplate === t.id ? 'border-[#8B5CF6]/40 bg-[#8B5CF6]/10 text-white' : 'border-white/10 text-[#9CA3AF] hover:border-white/20'}`} data-testid={`template-${t.id}`}>
                  <p className="font-medium">{t.name}</p>
                  <p className="text-[10px] text-[#64748B]">{t.category}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Audience Selection */}
          <div className="bg-[#1E1E3A]/80 backdrop-blur-xl border border-white/5 rounded-2xl p-6">
            <Label className="text-white font-semibold mb-3 flex items-center gap-2"><Filter size={14} /> Send To</Label>
            <div className="flex flex-wrap gap-2">
              {audiences.map(a => (
                <button key={a.value} onClick={() => setAudience(a.value)} className={`px-4 py-2 rounded-xl border text-sm font-medium transition-all ${audience === a.value ? 'text-white shadow-lg' : 'border-white/10 text-[#9CA3AF] hover:border-white/20'}`}
                  style={audience === a.value ? { borderColor: `${a.color}60`, background: `${a.color}15`, color: a.color } : {}}
                  data-testid={`audience-${a.value}`}
                >
                  {a.label}
                  {stats?.user_counts && !['all','single'].includes(a.value) && <span className="ml-1 text-[10px] opacity-60">({stats.user_counts[a.value] || '?'})</span>}
                  {a.value === 'all' && stats?.user_counts && <span className="ml-1 text-[10px] opacity-60">({stats.user_counts.all})</span>}
                </button>
              ))}
            </div>
            {/* Iter 175f — single-recipient email input. Appears only when
                the master selects the "Single Person" audience chip. */}
            {audience === 'single' && (
              <div className="mt-4">
                <Label className="text-[#9CA3AF] text-xs mb-1 block">Recipient email</Label>
                <Input
                  type="email"
                  value={singleEmail}
                  onChange={e => setSingleEmail(e.target.value)}
                  placeholder="e.g. user@example.com"
                  className="bg-white/5 border-white/10 text-white rounded-xl"
                  data-testid="email-single-recipient"
                />
                <p className="text-[10px] text-[#64748B] mt-1.5">Sends only to this address. Works for any email — does not need to be a registered user.</p>
              </div>
            )}
          </div>

          {/* Email Content */}
          <div className="bg-[#1E1E3A]/80 backdrop-blur-xl border border-white/5 rounded-2xl p-6 space-y-4">
            <div>
              <Label className="text-[#9CA3AF] text-xs mb-1 block">Subject Line</Label>
              <Input value={subject} onChange={e => setSubject(e.target.value)} placeholder="e.g. Exciting news from EntrepreNatives!" className="bg-white/5 border-white/10 text-white rounded-xl" data-testid="email-subject" />
            </div>
            <div>
              <Label className="text-[#9CA3AF] text-xs mb-1 block">Title (shown in email body)</Label>
              <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Big Announcement!" className="bg-white/5 border-white/10 text-white rounded-xl" data-testid="email-title" />
            </div>
            <div>
              <Label className="text-[#9CA3AF] text-xs mb-1 block">Content (HTML supported)</Label>
              <Textarea value={content} onChange={e => setContent(e.target.value)} placeholder="Write your email content here. You can use basic HTML tags like <b>bold</b>, <br/> for line breaks, <ul><li>lists</li></ul>, etc." className="bg-white/5 border-white/10 text-white rounded-xl min-h-[150px]" data-testid="email-content" />
            </div>
          </div>

          {/* Send Buttons */}
          <div className="flex gap-3">
            <Button onClick={() => handleSend(true)} disabled={sending} variant="outline" className="border-white/10 text-[#9CA3AF] hover:text-white" data-testid="send-test-btn">
              <Eye size={14} className="mr-2" /> {sending ? 'Sending...' : 'Send Test (to me)'}
            </Button>
            <Button onClick={() => handleSend(false)} disabled={sending} className="flex-1 bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white font-bold" data-testid="send-all-btn">
              {sending ? <Loader2 size={16} className="animate-spin mr-2" /> : <Send size={16} className="mr-2" />}
              {sending ? 'Sending...' : `Send to ${audience === 'all' ? 'All Users' : audience === 'single' ? (singleEmail || 'recipient') : audiences.find(a => a.value === audience)?.label}`}
            </Button>
          </div>
        </div>
      )}

      {tab === 'history' && (
        <div className="space-y-3">
          {history.length === 0 ? (
            <div className="text-center py-16">
              <Mail size={40} className="mx-auto text-[#64748B] mb-3" />
              <p className="text-[#9CA3AF]">No emails sent yet</p>
              <p className="text-[#64748B] text-sm">Compose your first campaign above</p>
            </div>
          ) : (
            history.map(c => (
              <div key={c.campaign_id} className="bg-[#1E1E3A]/80 backdrop-blur-xl border border-white/5 rounded-xl p-4 flex items-center gap-4" data-testid={`campaign-${c.campaign_id}`}>
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${c.status === 'completed' ? 'bg-green-500/20' : 'bg-yellow-500/20'}`}>
                  {c.status === 'completed' ? <CheckCircle size={18} className="text-green-400" /> : <Clock size={18} className="text-yellow-400" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium truncate">{c.subject || c.template_id || 'Untitled'}</p>
                  <div className="flex items-center gap-3 text-[10px] text-[#64748B] mt-0.5">
                    <span>{c.audience}</span>
                    <span>{c.test_only ? 'Test' : `${c.sent_count}/${c.total_recipients} sent`}</span>
                    {c.failed_count > 0 && <span className="text-red-400">{c.failed_count} failed</span>}
                    <span>{c.created_at ? new Date(c.created_at).toLocaleDateString() : ''}</span>
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-white font-mono text-sm font-bold">{c.sent_count}</p>
                  <p className="text-[10px] text-[#64748B]">sent</p>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
